import { Divider, Tab, TabGroup } from '@nike/eds'
import { type CellContext } from '@tanstack/react-table'
import { usePerformRasnActionMutation, useGetRasnQuery, useGetRasnEventsQuery } from 'api/rasn'
import { type Breadcrumb, Breadcrumbs } from 'components/breadcrumbs'
import { ConfirmationModal } from 'components/confirmation-modal'
import { CloseRasnIcon, RetriggerRasnEvent } from 'components/icon'
import { RetriggerRasnEventsModal } from 'components/retrigger-rasn-events-modal'
import { TableView, type TableColumn } from 'components/tableview'
import { ColumnType } from 'components/tableview/Filter'
import { CustomError } from 'pages/error-pages'
import { useEffect, useMemo, useState } from 'react'
import { showSnackbar } from 'redux/slices/globalState'
import { dispatch, useAppSelector } from 'redux/store'
import { useParams } from 'react-router-dom'
import {
  Division,
  type ErrorType,
  GoodsholderStatus,
  type RasnGoodsholder,
  RasnEventType,
  RasnStatus,
  UnitStatus,
  type RasnEvent,
  getSimpleGrades
} from 'types'
import { isImpact } from 'utils/dcContext'
import { getEnumValues } from 'utils/utils'

import './RasnDetailsView.scss'
import { RasnEventPayloadVisualizer } from './RasnEventPayloadVisualizer'
import { AuthRole } from 'auth/const'

export const RasnDetails = () => {
  const user = useAppSelector((state) => state.globalState.user)

  const [tableColumns, setTableColumns] = useState<TableColumn[]>([])
  const [tableData, setTableData] = useState<any[]>([])
  const [customFilters, setCustomFilters] = useState<Record<string, any>>({})
  const { rasnId } = useParams()
  if (!rasnId) { throw new Error('RASN ID is required') }
  const [activeTab, setActiveTab] = useState('tab-units')
  const [showRetriggerModal, setShowRetriggerModal] = useState(false)
  const [rasnEventForPayload, setRasnEventForPayload] = useState<null | RasnEvent>(null)
  const { data: rasnDetails, isLoading: isRasnLoading, isError: isRasnError, error: rasnError } = useGetRasnQuery(rasnId)
  const { data: events, isLoading: isEventsLoading, refetch: refetchRasnEvents } = useGetRasnEventsQuery(rasnId, {
    skip: activeTab !== 'tab-events',
    pollingInterval: activeTab === 'tab-events'
      ? showRetriggerModal ? 10000 : 2000
      : undefined
  })
  const [performRasnAction] = usePerformRasnActionMutation()

  const renderNullableSkuField = (context: CellContext<any, any>) => {
    const { row, column } = context

    if (row.getValue(column.id)) {
      return <div>{row.getValue(column.id)}</div>
    } else {
      return <div
        title='This SKU is present in the RASN, but U90 was unable to map it to known data'
        className='eds-color--text-danger'
      >
        ⚠️ missing data
      </div>
    }
  }

  const skuColumns = useMemo<TableColumn[]>(() => [
    { id: 'sku', header: 'SKU', type: ColumnType.TEXT, columnWidth: 6 },
    { id: 'upc', header: 'UPC', type: ColumnType.TEXT, columnWidth: 8, renderFn: renderNullableSkuField },
    { id: 'division', header: 'Division', type: ColumnType.ENUM, enumValues: getEnumValues(Division), columnWidth: 8, renderFn: renderNullableSkuField },
    { id: 'silhouette', header: 'Silhouette', type: ColumnType.TEXT, columnWidth: 12, renderFn: renderNullableSkuField },
    { id: 'hashable', header: 'Hash', type: ColumnType.BOOLEAN, columnWidth: 1 },
    { id: 'expectedQuantity', header: 'Expected', type: ColumnType.NUMBER, columnWidth: 5 },
    { id: 'actualQuantity', header: 'Handled', type: ColumnType.NUMBER, columnWidth: 5 }
  ], [])
  const unitColumns = useMemo<TableColumn[]>(() => [
    { id: 'sku', header: 'SKU', type: ColumnType.TEXT, columnWidth: 10 },
    { id: 'upc', header: 'UPC', type: ColumnType.TEXT, columnWidth: 8 },
    { id: 'division', header: 'Division', type: ColumnType.ENUM, enumValues: getEnumValues(Division), columnWidth: 7 },
    { id: 'silhouette', header: 'Silhouette', type: ColumnType.TEXT, columnWidth: 10 },
    { id: 'hashable', header: 'Hash', type: ColumnType.BOOLEAN, columnWidth: 3 },
    { id: 'quantity', header: 'Qty', type: ColumnType.NUMBER, columnWidth: 4 },
    ...(isImpact
      ? []
      : [
        { id: 'coo', header: 'CoO', type: ColumnType.TEXT, columnWidth: 5 }
      ]),
    { id: 'simpleGrade', header: 'Grade', type: ColumnType.ENUM, enumValues: getSimpleGrades(), columnWidth: 4 },
    { id: 'gradeReason', header: 'Reason', type: ColumnType.TEXT, columnWidth: 12 },
    { id: 'unitHandlingStation', header: 'Station', type: ColumnType.TEXT, columnWidth: 6 },
    { id: 'tote', header: 'Tote', type: ColumnType.TEXT, columnWidth: 11 },
    { id: 'receivedGoodsHolder', header: 'Goodsholder', type: ColumnType.TEXT, columnWidth: 11 },
    { id: 'status', header: 'Status', type: ColumnType.ENUM, enumValues: getEnumValues(UnitStatus), columnWidth: 6 },
    { id: 'modifiedBy', header: 'Athlete*', type: ColumnType.TEXT, columnWidth: 6 },
    { id: 'modifiedTimestamp', header: 'Modified', type: ColumnType.DATE, columnWidth: 12 }
  ], [])
  const goodsholderColumns = useMemo<TableColumn[]>(() => [
    { id: 'lpn', header: 'Goodsholder LPN', type: ColumnType.TEXT },
    { id: 'trackingId', header: 'Tracking ID', type: ColumnType.TEXT },
    { id: 'actualQuantity', header: 'Handled', type: ColumnType.NUMBER },
    { id: 'status', header: 'Status', type: ColumnType.ENUM, enumValues: getEnumValues(GoodsholderStatus) },
    { id: 'modifiedBy', header: 'Last modified by', type: ColumnType.TEXT },
    { id: 'modifiedTimestamp', header: 'Last modified time', type: ColumnType.DATE }
  ], [])

  const eventColumns = useMemo<TableColumn[]>(() => [
    { id: 'type', header: 'Event Type', type: ColumnType.ENUM, enumValues: getEnumValues(RasnEventType) },
    { id: 'timestamp', header: 'Timestamp', type: ColumnType.DATE },
    { id: 'externalId', header: 'External ID', type: ColumnType.TEXT },
    { id: 'version', header: 'Version', type: ColumnType.TEXT }
  ], [])

  useEffect(() => {
    if (!rasnDetails) return
    let columns: TableColumn[] = []
    let tableData: any[] = []
    switch (activeTab) {
      case 'tab-sku':
        setCustomFilters({})
        columns = skuColumns
        tableData = rasnDetails.skus
        break
      case 'tab-units':
        setCustomFilters({})
        columns = unitColumns.map(column => ({
          ...column,
          initialFilterValue: customFilters[column.id] ?? column.initialFilterValue
        }))
        tableData = rasnDetails.units.map((unit) => ({
          ...unit
        }))
        break
      case 'tab-goodsholders':
        columns = goodsholderColumns
        tableData = rasnDetails.goodsholders
        break
      case 'tab-events':
        refetchRasnEvents()
        setCustomFilters({})
        columns = eventColumns
        tableData = events || []
        break
    }
    setTableColumns(columns)
    setTableData(tableData)
  }, [activeTab, rasnDetails, events])

  const goToUnitsAndFilterAutomaticallyOnGoodsholder = (goodsholder: string) => {
    setCustomFilters({ receivedGoodsHolder: goodsholder })
    setActiveTab('tab-units')
  }

  if (isRasnError) {
    const errorMessage = (rasnError as ErrorType).data.message
    return <CustomError error={errorMessage} />
  }

  if (isRasnLoading || !rasnDetails) {
    return <div>Loading...</div>
  }

  const breadcrumbs: Breadcrumb[] = [
    { name: 'RASN', path: '/' },
    { name: `${rasnDetails.number}` }
  ]

  return (
    <div className="m-4">
      <div className="header">
        <Breadcrumbs crumbs={breadcrumbs} />
        <div className="header-bar">
          <p><span className="label">Customer</span>{rasnDetails.customerName || 'Unknown'}</p>
          <p><span className="label">Channel</span>{rasnDetails.channel || 'Unknown'}</p>
          <p><span className="label">Status</span>{rasnDetails.status || 'Unknown'}</p>
          <p><span className="label">Hold code</span>{rasnDetails.holdCode || 'None'}</p>
          <p><span className="label">End date</span>{rasnDetails.extendedAttributes?.returnsEndDate || 'Unknown'}</p>
          <p><span className="label">Expected</span>{rasnDetails.totalExpectedUnits === 0 ? '⚠️ unknown' : rasnDetails.totalExpectedUnits}</p>
          <p><span className="label">Handled</span>{rasnDetails.totalHandledUnits}</p>
          <p></p>
        </div>
        {user && !user.isAtMost(AuthRole.VIEW) &&
          <div className="my-6 -mb-6 flex justify-end gap-4">
            {rasnDetails.holdCode && user.hasSpecific(AuthRole.MODIFY_RASN) && (
              <ConfirmationModal
                onTrigger={() => { performRasnAction({ rasnNumber: rasnId, action: 'unlock' }) }}
                title='Unlock RASN'
                message="Are you sure you want to unlock this RASN?"
                icon='Unlock'
                confirmText='Unlock'
              />
            )}
            {rasnDetails.status !== RasnStatus.CLOSED && rasnDetails.status !== RasnStatus.CANCELLED && user.hasSpecific(AuthRole.MODIFY_RASN) && (
              <ConfirmationModal
                onTrigger={async () => {
                  if (rasnDetails.rasnFamilyId) {
                    dispatch(showSnackbar({
                      message: `RASN belongs to Family RSO ${rasnDetails.rasnFamilyId}. Please close the Family RSO instead.`,
                      status: 'error'
                    }))
                    return
                  }
                  const result = await performRasnAction({ rasnNumber: rasnId, action: 'close' })
                  if ('data' in result) {
                    dispatch(showSnackbar({
                      message: `RASN ${rasnDetails.number} has been closed.`,
                      status: 'success'
                    }))
                  }
                }}
                title='Close RASN'
                message="Are you sure you want to close this RASN?"
                icon={CloseRasnIcon}
                confirmText='Close'
              />
            )}
            {rasnDetails.status === RasnStatus.CLOSED && user.hasSpecific(AuthRole.RETRIGGER_EVENTS) && (
              <div className="cursor-pointer scale-[2.3] ml-3 pt-2 mt-2" onClick={() => { setShowRetriggerModal(true) }}>
                {RetriggerRasnEvent}
              </div>
            )}
          </div>
        }
      </div>
      <TabGroup
        className={'pt-6'}
        activeId={activeTab}
        name="tab-button-group"
        onChange={e => { setActiveTab(e.target.id) }}>
        <Tab id="tab-units">Units</Tab>
        <Tab id="tab-sku">SKUs</Tab>
        <Tab id="tab-goodsholders">GoodsHolders</Tab>
        <Tab id="tab-events">Events</Tab>
      </TabGroup>
      <Divider className='bg-black p-px' />
      <TableView
        columns={tableColumns}
        data={tableData}
        loading={activeTab === 'tab-events' && isEventsLoading}
        actions={
          activeTab === 'tab-goodsholders'
            ? [{
              label: 'View',
              icon: 'Zoomin',
              hoverText: 'Goodsholder units',
              onClick: (row: RasnGoodsholder) => { goToUnitsAndFilterAutomaticallyOnGoodsholder(row.lpn) }
            }]
            : activeTab === 'tab-events'
              ? [{
                label: 'View Payload',
                icon: 'Show',
                hoverText: 'View event payload',
                onClick: (row: RasnEvent) => { setRasnEventForPayload(row) }
              }]
              : []
        }
      />
      <RetriggerRasnEventsModal
        rasnNumber={rasnId}
        showModal={showRetriggerModal}
        setShowModal={setShowRetriggerModal}
      />
      {rasnEventForPayload !== null &&
        <RasnEventPayloadVisualizer rasnEvent={rasnEventForPayload} onClose={() => { setRasnEventForPayload(null) }} />
      }
    </div>
  )
}
